/**
 * The aim guide (UI-001) — a straightedge drawn on the cloth.
 *
 * Deliberately a view of geometry and nothing else: it is handed a line and a ghost
 * position and draws them. It does not know what the shot will do, and it must never
 * be given anything derived from a `SimResult` (ARCHITECTURE.md §5.1).
 *
 * The line stops at first contact. It does not continue past the object ball, because
 * where the cue ball goes after contact depends on spin, throw and cushion behaviour —
 * showing it would be either a lie or the answer.
 */
import type { Viewport } from './viewport.ts';

/** Exactly the fields `firstContact` produces; see game/aim.ts. */
export interface AimOverlay {
  readonly fromXM: number;
  readonly fromYM: number;
  readonly contactXM: number;
  readonly contactYM: number;
  /** Centre of the object ball, when one is on the line. */
  readonly objectXM: number | undefined;
  readonly objectYM: number | undefined;
}

export interface AimPalette {
  readonly guide: string;
  readonly ghost: string;
}

/**
 * How the guide shows power (UI-015, testers 2026-09-27: "the lines further apart or the
 * line to the ball"). With the cue on, the cue stands off the ball further the harder the
 * shot; with it off in Settings, the guide's dashes spread instead. Power, not the shot's
 * outcome: nothing here comes from a `SimResult`.
 */
export interface AimStyle {
  /** 0–1, as set on the power slider. */
  readonly power: number;
  /** Draw the pool cue (Settings, on by default). */
  readonly cue: boolean;
  /** The cue's length, in ball radii (UI-018, from Settings). */
  readonly lengthR: number;
  /**
   * Side spin as the spin ball shows it, in R, + to the player's right (UI-016). The cue
   * sits that far off the ball's centre line, where it would strike. Screen frame: what
   * the player set, not the table-frame value the shot is taken with.
   */
  readonly sideR: number;
}

/** The gap between cue tip and ball, in ball radii: close at a tap, well back at full. */
export function cuePullBackR(power: number): number {
  const p = power > 1 ? 1 : power > 0 ? power : 0;
  return 0.6 + 5.4 * p;
}

/** The gap between the guide's dashes, in ball radii, when the cue is off. */
export function dashGapR(power: number): number {
  const p = power > 1 ? 1 : power > 0 ? power : 0;
  return 0.35 + 1.65 * p;
}

export interface CuePalette {
  readonly tip: string;
  readonly ferrule: string;
  readonly shaft: string;
  /** The joint between shaft and butt (UI-019). */
  readonly ring: string;
  readonly butt: string;
  readonly shadow: string;
}

export function readCuePalette(): CuePalette {
  const style = getComputedStyle(document.documentElement);
  const token = (name: string): string => style.getPropertyValue(name).trim();
  return {
    tip: token('--color-cue-tip'),
    ferrule: token('--color-cue-ferrule'),
    shaft: token('--color-cue-shaft'),
    ring: token('--color-cue-ring'),
    butt: token('--color-cue-butt'),
    shadow: token('--color-cue-shadow'),
  };
}

export function readAimPalette(): AimPalette {
  const style = getComputedStyle(document.documentElement);
  return {
    guide: style.getPropertyValue('--color-aim-guide').trim(),
    ghost: style.getPropertyValue('--color-ghost-ball').trim(),
  };
}

/** How far past the object ball the line of centres is drawn, in ball radii. */
const CENTRE_LINE_R = 4;

export function drawAim(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  palette: AimPalette,
  radiusM: number,
  aim: AimOverlay,
  style: AimStyle = { power: 0.5, cue: true, sideR: 0, lengthR: cueLengthR(0.5) },
): void {
  const radius = viewport.lengthPx(radiusM);
  const fromX = viewport.toScreenX(aim.fromXM, aim.fromYM);
  const fromY = viewport.toScreenY(aim.fromXM, aim.fromYM);
  const toX = viewport.toScreenX(aim.contactXM, aim.contactYM);
  const toY = viewport.toScreenY(aim.contactXM, aim.contactYM);

  ctx.save();

  ctx.strokeStyle = palette.guide;
  ctx.lineWidth = Math.max(1, radius * 0.12);
  ctx.setLineDash([radius * 0.5, radius * (style.cue ? 0.5 : dashGapR(style.power))]);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(toX, toY);
  ctx.stroke();

  // The ghost ball: where the cue ball's centre would be at the moment of contact.
  ctx.setLineDash([]);
  ctx.fillStyle = palette.ghost;
  ctx.beginPath();
  ctx.arc(toX, toY, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = palette.guide;
  ctx.lineWidth = Math.max(1, radius * 0.08);
  ctx.stroke();

  /*
   * The line of centres, when a ball is struck: the direction an object ball leaves in
   * for a full-face hit. It is geometry — the line through both centres — not a
   * prediction, and throw will shift the real departure slightly off it.
   */
  if (aim.objectXM === undefined || aim.objectYM === undefined) {
    ctx.restore();
    return;
  }

  const objX = viewport.toScreenX(aim.objectXM, aim.objectYM);
  const objY = viewport.toScreenY(aim.objectXM, aim.objectYM);
  const dx = objX - toX;
  const dy = objY - toY;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length > 0) {
    const reach = radius * CENTRE_LINE_R;
    ctx.globalAlpha = 0.6;
    ctx.beginPath();
    ctx.moveTo(objX, objY);
    ctx.lineTo(objX + (dx / length) * reach, objY + (dy / length) * reach);
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * The cue's length at 100% on the Settings slider, in ball radii (a real cue is about 50).
 * UI-018 (owner, 2026-09-27): the player sets the length; the default is half of this.
 */
export const CUE_MAX_LENGTH_R = 30;

/** The cue's length in ball radii for a Settings value of 0–1 (`Settings.cueLength`). */
export function cueLengthR(fraction: number): number {
  const f = fraction > 1 ? 1 : fraction > 0 ? fraction : 0;
  return CUE_MAX_LENGTH_R * f;
}

/**
 * The pool cue (UI-015): behind the cue ball, on the line of aim, standing off by
 * `cuePullBackR(power)`. Drawn over the balls, as a cue lies above them. A tapered shaft:
 * a chalked tip, a white ferrule, a pale maple shaft and a dark butt, with a soft shadow.
 */
export function drawCue(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  palette: CuePalette,
  radiusM: number,
  aim: AimOverlay,
  power: number,
  sideR = 0,
  lengthR = cueLengthR(0.5),
): void {
  const radius = viewport.lengthPx(radiusM);
  const ballX = viewport.toScreenX(aim.fromXM, aim.fromYM);
  const ballY = viewport.toScreenY(aim.fromXM, aim.fromYM);
  const aheadX = viewport.toScreenX(aim.contactXM, aim.contactYM) - ballX;
  const aheadY = viewport.toScreenY(aim.contactXM, aim.contactYM) - ballY;
  const length = Math.sqrt(aheadX * aheadX + aheadY * aheadY);
  if (!(length > 0)) return;
  // Unit vectors: back along the cue (away from the shot), and across it.
  const bx = -aheadX / length;
  const by = -aheadY / length;
  const nx = -by;
  const ny = bx;
  // The player's right, looking along the shot, on a screen whose y grows downward.
  const shiftX = (aheadY / length) * -1 * radius * sideR;
  const shiftY = (aheadX / length) * radius * sideR;
  const start = radius * (1 + cuePullBackR(power));
  if (!(lengthR > 0)) return;
  const total = radius * lengthR;
  // UI-017: about three-quarters of the first width, lighter over the table.
  const tipHalf = radius * 0.18;
  const buttHalf = radius * 0.37;
  const half = (t: number): number => tipHalf + (buttHalf - tipHalf) * t;

  /** One band of the cue, from fraction `a` to `b` of its length. */
  const band = (a: number, b: number, fill: string, dx = 0, dy = 0): void => {
    const ax = ballX + shiftX + bx * (start + total * a) + dx;
    const ay = ballY + shiftY + by * (start + total * a) + dy;
    const cx = ballX + shiftX + bx * (start + total * b) + dx;
    const cy = ballY + shiftY + by * (start + total * b) + dy;
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(ax + nx * half(a), ay + ny * half(a));
    ctx.lineTo(cx + nx * half(b), cy + ny * half(b));
    ctx.lineTo(cx - nx * half(b), cy - ny * half(b));
    ctx.lineTo(ax - nx * half(a), ay - ny * half(a));
    ctx.closePath();
    ctx.fill();
  };

  ctx.save();
  ctx.setLineDash([]);
  // No fade (the owner found it odd, UI-018): the cue simply ends, at the length set.
  band(0, 1, palette.shadow, radius * 0.3, radius * 0.4);
  const tip = Math.min(0.3 / lengthR, 0.1);
  const ferrule = Math.min(tip + 1.1 / lengthR, 0.3);
  band(0, tip, palette.tip);
  band(tip, ferrule, palette.ferrule);
  band(ferrule, 0.64, palette.shaft);
  band(0.64, 0.68, palette.ring);
  band(0.68, 1, palette.butt);
  ctx.restore();
}
